import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { isEqual, omit } from "lodash"
import moment from "moment"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { getCalendarBookings } from "store/calendar"
import { useCalendarDateRange } from "pagesV2/pro/Calendar/useCalendarDateRange"
import { routes } from "router"
import Calendar from "ui/Calendar"
import useSearchFilter from "hooks/useSearchFilter"

/**
 * Crew Member Calendar Wrapper
 * Fetches calendar data and filters by crew member, then passes to reusable Calendar component
 */
const CrewMemberCalendar = ({ crewMemberId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [params, updateSearchFilters] = useSearchFilter({
    accessAttributes: ["range", "date"]
  })
  const [paramsState, setParamsState] = useState(params)

  const dates = useCalendarDateRange(params)

  const { bookings, loading, calendarTimeOff, journalNoteDates } = useSelector((state) => state.calendar)

  const changeHandler = useCallback(
    ({ target, extra }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value,
        ...(extra && { [extra.name]: extra.value })
      })),
    []
  )

  useEffect(() => {
    if (isEqual(paramsState, omit(params, []))) return
    updateSearchFilters(paramsState)
  }, [params, paramsState, updateSearchFilters])

  // Fetch calendar data when date range changes
  useEffect(() => {
    if (!dates?.start || !dates?.end) return

    const data = {
      "q[date_gteq]": dates.start,
      "q[date_lteq]": dates.end,
      "q[crew_member_id_eq]": crewMemberId
    }

    dispatch(getCalendarBookings(data))
    // dispatch(getCalendarTimeOff(data))
  }, [dates?.start, dates?.end, crewMemberId, dispatch])

  // Filter bookings by crew member ID
  const filteredBookings = useMemo(() => {
    return (
      bookings?.filter((booking) => {
        return booking?.crew_member_ids?.includes(Number.parseInt(crewMemberId, 10))
      }) || []
    )
  }, [bookings, crewMemberId])

  // Filter time off by crew member ID
  const filteredTimeOff = useMemo(() => {
    return (
      calendarTimeOff?.filter((timeOff) => {
        return timeOff?.crew_member_id === Number.parseInt(crewMemberId, 10)
      }) || []
    )
  }, [calendarTimeOff, crewMemberId])

  const handleAddBooking = () => {
    navigate(routes.guideBookingNewPath())
  }

  return (
    <Calendar
      bookingsData={filteredBookings}
      journalNoteDates={journalNoteDates}
      timeOffData={filteredTimeOff}
      loading={loading}
      date={params.date || moment().format(DATE_FORMAT)}
      range={params.range || "week"}
      onChangeFilters={changeHandler}
      showControls={{
        addBooking: true,
        journal: false,
        scheduleTimeOff: false
      }}
      onAddBooking={handleAddBooking}
    />
  )
}

CrewMemberCalendar.propTypes = {
  crewMemberId: PropTypes.string.isRequired
}

export default CrewMemberCalendar
